import { Control, FieldValues, Path, PathValue } from "react-hook-form";

import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { UnitName } from "#shared/view/designSystem/inputs/units";
import { useRecommendedInputStyles } from "#shared/view/designSystem/inputs/useRecommendedInputStyles";
import { useRecommendedValue } from "#shared/view/designSystem/inputs/useRecommendedValue";

type Props<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  onClickWhy?: () => void;
  defaultValue?: number | undefined;
  placeholder?: string;
  isDecimal?: boolean;
  control: Control<T>;
  required?: boolean;
  disabled?: boolean;
  computedValue?: { value: PathValue<T, Path<T>> | null; message?: string };
  companyValue?: { value: PathValue<T, Path<T>> | null; message?: string };
  unit?: UnitName;
  readOnly?: boolean;
  precision?: number;
  onChange?: (value: number) => void;
  onBlur?: () => void;
  unitFactors?: { value: number; unit: UnitName }[];
  inlineLabel?: boolean;
  small?: boolean;
  defaultUnitFactor?: { value: number; unit: UnitName };
  dependencies?: Path<T>[];
  style?: React.CSSProperties;
  rightSectionRef?: React.RefObject<HTMLDivElement | null>;
  onChangeFactor?: (unit?: UnitName) => void;
};

export function RecommendedNumberInputController<T extends FieldValues>({
  name,
  control,
  label,
  onClickWhy,
  defaultValue,
  placeholder,
  required,
  disabled,
  computedValue,
  companyValue,
  unit,
  unitFactors,
  defaultUnitFactor,
  dependencies,
  style,
  rightSectionRef,
  onChangeFactor,
  ...props
}: Props<T>) {
  const styles = useRecommendedInputStyles({
    companyValue,
    computedValue,
    name,
    precision: props.precision || 2,
  });

  useRecommendedValue({ name, companyValue, computedValue, dependencies });

  return (
    <NumberInputController
      name={name}
      label={label}
      placeholder={placeholder}
      companyValue={companyValue}
      computedValue={computedValue}
      control={control}
      required={required}
      defaultValue={defaultValue}
      disabled={disabled}
      onClickWhy={onClickWhy}
      unit={unit}
      unitFactors={unitFactors}
      defaultUnitFactor={defaultUnitFactor}
      styles={styles}
      style={style}
      rightSectionRef={rightSectionRef}
      onChangeFactor={onChangeFactor}
      {...props}
    />
  );
}
